// Card-brand acceptance-mark tile, shown in place of the brand name on a
// payment row. Picked by payment type, not by name: Visa, Mastercard, AMEX,
// Debit (Interac) and Discover. Type 232 was e-Transfer before the backend
// reassigned it to Discover, so it only gets the Discover tile when the
// row's own label says Discover. A wallet payment shows the tile too,
// with its Google Pay / Apple Pay mark (WalletMark) after it.

import amex from '@/assets/cards/amex.svg'
import discover from '@/assets/cards/discover.svg'
import interac from '@/assets/cards/interac.svg'
import mastercard from '@/assets/cards/mastercard.svg'
import visa from '@/assets/cards/visa.svg'
import { PAYMENT_TYPES } from '@/api/receipts'
import { WalletMark } from '@/components/WalletMark'

type Brand = 'visa' | 'mastercard' | 'amex' | 'interac' | 'discover'

const TILES: Record<Brand, { src: string; name: string }> = {
  visa: { src: visa, name: 'Visa' },
  mastercard: { src: mastercard, name: 'Mastercard' },
  amex: { src: amex, name: 'AMEX' },
  interac: { src: interac, name: 'Interac' },
  discover: { src: discover, name: 'Discover' },
}

function cardBrandFor(paymentType: unknown, label: string): Brand | null {
  switch (Number(paymentType)) {
    case PAYMENT_TYPES.VISA: return 'visa'
    case PAYMENT_TYPES.MASTER_CARD: return 'mastercard'
    case PAYMENT_TYPES.AMEX: return 'amex'
    case PAYMENT_TYPES.DEBIT: return 'interac'
    case PAYMENT_TYPES.E_TRANSFER: return /discover/i.test(label) ? 'discover' : null
    default: return null
  }
}

/** The payment's brand tile (or its name when it has none), then its
 *  wallet mark and masked last 4: "[VISA] [G] •••• 8859". */
export function PaymentMethodLabel({
  label,
  paymentType,
  wallet,
  last4,
}: {
  label: string
  paymentType: unknown
  wallet?: string | null
  last4?: string | null
}) {
  const brand = cardBrandFor(paymentType, label)
  const tile = brand ? TILES[brand] : null
  return (
    <>
      {tile ? (
        <img src={tile.src} width={30} height={20} alt={tile.name} title={tile.name} className="shrink-0 object-contain rounded-[3px] ring-1 ring-gray-200" />
      ) : (
        label
      )}
      {(last4 || wallet) && (
        // With a tile the digits are the row's only text, so they take the
        // name's size and colour; next to a name they stay a muted aside.
        <span className={`tabular-nums flex items-center gap-1 ${tile ? 'font-semibold' : 'text-gray-400 text-xs'}`}>
          {!tile && '· '}
          <WalletMark wallet={wallet} />
          {last4 && `•••• ${last4}`}
        </span>
      )}
    </>
  )
}
